import React, { useEffect } from "react";
import { Container, Card, CardBody, Row, Col } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link } from "react-router-dom";
import { bulkReset } from "../../../store/bulk-schedule/actions";
import BreadcrumbLink from "../../../components/Common/BreadcrumbLink";
import StepUpload from "./steps/StepUpload";
import StepValidating from "./steps/StepValidating";
import StepErrors from "./steps/StepErrors";
import StepSummary from "./steps/StepSummary";
import StepDone from "./steps/StepDone";
import i18n from "../../../i18n";
import { orgId } from "../../../constants/utils";

const selectBulk = createSelector(
  (state) => state.BulkSchedule,
  (bs) => ({ taskStatus: bs.taskStatus, taskId: bs.taskId, uploading: bs.uploading })
);

const STEPS = [
  { key: null,          label: "Cargar archivo"   },
  { key: "validating",  label: "Validando"         },
  { key: "error",       label: "Errores"           },
  { key: "confirmed",   label: "Confirmar"         },
  { key: "processing",  label: "Procesando"        },
  { key: "done",        label: "Completado"        },
];

const BulkScheduleWizard = () => {
  document.title = `${i18n.t("MassProgramming")} - ${orgId}`;
  const dispatch = useDispatch();
  const { taskStatus, uploading } = useSelector(selectBulk);

  useEffect(() => {
    return () => { dispatch(bulkReset()); };
  }, []);

  const activeStep = uploading ? "validating" : (taskStatus ?? null);

  const renderStep = () => {
    switch (activeStep) {
      case null:         return <StepUpload />;
      case "validating": return <StepValidating />;
      case "error":      return <StepErrors />;
      case "confirmed":  return <StepSummary />;
      case "processing": return <StepDone />;
      case "done":       return <StepDone />;
      default:           return <StepUpload />;
    }
  };

  const currentIdx = STEPS.findIndex((s) => s.key === activeStep);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col className="text-end">
            <Link to="/services/bulk-schedule/tasks" className="text-muted" style={{ fontSize: 13 }}>
              <i className="mdi mdi-history me-1" />{i18n.t("ViewHistory")}
            </Link>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <BreadcrumbLink title={i18n.t("Services")} link='/services/request' breadcrumbItem={i18n.t("MassiveServiceLoad")} /> 
          </Col>
        </Row>
        <Row>
          <Col md={8} className="mb-3">
            <Card>
              <CardBody>
                {renderStep()}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="steps-card">
              <CardBody className="py-3 px-3">
                <ul className="list-unstyled mb-0">
                  {STEPS.filter((s) => s.key !== "error" && s.key !== "processing").map((step, idx) => {
                    const stepIdx = STEPS.findIndex((s) => s.key === step.key);
                    const isDone = currentIdx > stepIdx;
                    const isActive = currentIdx === stepIdx;
                    return (
                      <li
                        key={step.key ?? "upload"}
                        className={`d-flex align-items-center gap-3 py-2 ${isActive ? 'active-step' : ''}`}
                        style={{ 
                          borderBottom: idx < STEPS.filter(s => s.key !== "error" && s.key !== "processing").length - 1 ? "1px solid #f0f0f0" : "none",
                          transition: "all 0.3s ease"
                        }}
                      >
                        <div className="step-icon-wrapper">
                          {isDone ? (
                            <i className="mdi mdi-check-circle" style={{ fontSize: 22, color: "#34C38F" }} />
                          ) : isActive ? (
                            <div className="pulse-icon">
                              <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 22, color: "#FF5F00" }} />
                            </div>
                          ) : (
                            <i className="mdi mdi-circle-outline" style={{ fontSize: 22, color: "#ADB5BD" }} />
                          )}
                        </div>
                        <span style={{ 
                          fontSize: 14, 
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "#FF5F00" : isDone ? "#34C38F" : "#6C757D"
                        }}>
                          {step.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BulkScheduleWizard;
